import { Box, Typography, useMediaQuery } from "@mui/material";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { PaginationOptions } from "../../../../../../../components/common/BriaTable/BriaTable.tsx";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import TableLayout from "../../../../../../../layout/TableLayout/TableLayout.tsx";
import { TgModel } from "../../../../../Models/store/tg-model-store.tsx";
import { useModelTableHelpers } from "../hooks/useModelTableHelpers.tsx";
import columnStyles from "../shared/TgModelsTableColumns.module.scss";
import { TgModelTableRowType } from "../types.ts";
import styles from "./FullyAutomatedModelsTable.module.scss";

interface FullyAutomatedModelsTableProps {
	models: TgModel[];
	loadingRow?: { data: TgModel; column: string };
	setLoadingRow: (loadingRow: { data: TgModel; column: string } | undefined) => void;
	paginationOptions: PaginationOptions<TgModel>;
}

const FullyAutomatedModelsTable = observer(
	({ models, loadingRow, setLoadingRow, paginationOptions }: FullyAutomatedModelsTableProps) => {
		const { t } = useTranslation("tg", { keyPrefix: "models.dashboard" });
		const {
			getPossibleAction,
			getCreationBy,
			getIntegrateNowButton,
			getNameColumn,
			getStatusColumn,
			getActionsColumn,
			getFeedbackCountCell,
		} = useModelTableHelpers();
		const { tgStore } = useAppStore();
		const { tgProjectStore } = tgStore;

		const isSmallScreen = useMediaQuery("(max-width:1540px)");

		// Headers for Fully Automated Models table (responsive)
		const fullyAutomatedTableHeaderArray = isSmallScreen
			? [
					t("table.headers.name"),
					t("table.headers.id"),
					t("table.headers.creation_by"),
					t("table.headers.status"),
					t("table.headers.training_version"),
					t("table.headers.images_count"),
					t("table.headers.feedback_count"),
					t("table.headers.actions"),
					"",
				]
			: [
					t("table.headers.name"),
					t("table.headers.id"),
					t("table.headers.creation_by"),
					t("table.headers.status"),
					t("table.headers.training_version"),
					t("table.headers.images_count"),
					t("table.headers.feedback_count"),
					t("table.headers.actions"),
					t("table.headers.get_access"),
					t("table.headers.possible_action"),
				];

		const createFullyAutomatedRow = (model: TgModel): TgModelTableRowType => {
			const training_version = <Box>{t(`table.training_version.${model.training_version}`)}</Box>;
			const dataset = tgProjectStore.paginatedDatasets.items.find((d) => d.id === model?.dataset_id);
			const images_count = <Box>{dataset?.images_count ?? "-"}</Box>;

			return {
				key: model.id,
				data: (() => {
					const base: any = {
						name: getNameColumn(model),
						id: model.id,
						creation_by: getCreationBy(model),
						status: getStatusColumn(model),
						training_version: training_version,
						images_count: images_count,
						feedback_count: getFeedbackCountCell(model),
						actions: getActionsColumn(model),
					};
					if (isSmallScreen) {
						const setLoadingRowForCta = (loadingRow?: { data: TgModel; column: string }) =>
							setLoadingRow(loadingRow ? { ...loadingRow, column: "cta_stacked" } : undefined);
						base["cta_stacked"] = (
							<Box className={columnStyles.ctaStacked}>
								{getIntegrateNowButton(model)}
								{getPossibleAction(model, setLoadingRowForCta)}
							</Box>
						);
					} else {
						base["get_access"] = getIntegrateNowButton(model);
						base["possible_action"] = getPossibleAction(model, setLoadingRow);
					}
					return base;
				})(),
			};
		};

		const fullyAutomatedTableData = models.map((model) => createFullyAutomatedRow(toJS(model)));

		return (
			<Box className={styles.tableSection}>
				<Typography className={styles.tableTitle}>{t("fullyAutomatedTitle")}</Typography>
				<TableLayout
					headerArray={fullyAutomatedTableHeaderArray}
					tableData={fullyAutomatedTableData}
					paginationOptions={paginationOptions}
					loadingRow={loadingRow}
					hideHeader
					minTableHeight={"100px"}
					columnOrderKeys={
						isSmallScreen
							? [
									"name",
									"id",
									"creation_by",
									"status",
									"training_version",
									"images_count",
									"actions",
									"cta_stacked",
								]
							: [
									"name",
									"id",
									"creation_by",
									"status",
									"training_version",
									"images_count",
									"actions",
									"get_access",
									"possible_action",
								]
					}
					columnClassNames={{
						name: columnStyles.colName,
						id: columnStyles.colId,
						creation_by: columnStyles.colCreationBy,
						status: columnStyles.colStatus,
						actions: columnStyles.colActions,
						get_access: columnStyles.colGetAccess,
						possible_action: columnStyles.colPossibleAction,
						training_version: columnStyles.colTrainingVersion,
						images_count: columnStyles.colImagesCount,
						cta_stacked: columnStyles.colCtaStacked,
					}}
					loadingCellClassName={isSmallScreen ? columnStyles.ctaStackedCenterLoading : undefined}
					stickyHeader
				/>
			</Box>
		);
	},
);

export default FullyAutomatedModelsTable;
